import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider.jsx'
import { db } from '../data/db.js'
import Toast from '../components/Toast.jsx'
import BackButton from '../components/BackButton.jsx'
import ConfirmModal from '../components/ConfirmModal.jsx'
import { getBackupSettings, saveBackupSettings, createBackup, listBackups, deleteBackup, restoreBackup, getBackupFolderHandle, selectBackupFolder, listFolderBackups, deleteFolderBackup, restoreFolderBackup, downloadBackup } from '../data/backup.js'
import { supabase } from '../data/supabase.js'
import { getFinanceSettings, saveFinanceSettings } from '../data/financeSettings.js'

const SUPER_ADMIN_LOGIN = 'willian.costa7465@gmail.com'

export default function SettingsPage() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const isAdmin = session?.perfil === 'admin'
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [busy, setBusy] = useState(false)
  const [backupSettings, setBackupSettings] = useState({ enabled: true, interval: 'daily', lastBackup: null })
  const [backups, setBackups] = useState([])
  const [showBackups, setShowBackups] = useState(false)
  const [backupFolder, setBackupFolder] = useState(null)
  const [finSettings, setFinSettings] = useState({ enabled: true, hideValues: false })

  // Estados para modais de confirmação
  const [confirmModal, setConfirmModal] = useState({ open: false, type: '', title: '', message: '', onConfirm: null })
  const [installStatus, setInstallStatus] = useState({ checking: false, installed: false, canInstall: false })

  // Modal limpar banco online
  const [limparOnlineModal, setLimparOnlineModal] = useState(false)
  const [limparSenha, setLimparSenha] = useState('')
  const [limparBusy, setLimparBusy] = useState(false)
  const [limparErro, setLimparErro] = useState('')

  useEffect(() => {
    async function loadSettings() {
      const settings = await getBackupSettings()
      setBackupSettings(settings)
      const list = await listBackups()
      setBackups(list)
      const folder = await getBackupFolderHandle()
      setBackupFolder(folder)
      const fs = await getFinanceSettings()
      setFinSettings(fs)
    }
    loadSettings()

    // Verificar status de instalação do PWA
    const checkInstallStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true ||
                          document.referrer.includes('android-app://')
      const canInstall = !!window.deferredPrompt || !isStandalone
      setInstallStatus({ checking: false, installed: isStandalone, canInstall: canInstall })
    }
    checkInstallStatus()

    // Ouvir evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      window.deferredPrompt = e
      setInstallStatus(prev => ({ ...prev, canInstall: true }))
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  async function exportBackup() {
    setBusy(true)
    try {
      const data = {
        exportedAt: Date.now(),
        version: 4,
        produtos: await db.produtos.toArray(),
        funcionarios: await db.funcionarios.toArray(),
        movimentacoes: await db.movimentacoes.toArray(),
        users: await db.users.toArray(),
        auditLogs: await db.auditLogs.toArray(),
        settings: await db.settings.toArray()
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `backup-caixa-mestre-${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)
      setToastType('success')
      setToast('Backup exportado.')
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao exportar.')
    } finally {
      setBusy(false)
    }
  }

  async function importBackup(file) {
    setBusy(true)
    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // Verificar formato do backup (novo formato com 'dados' ou antigo com 'version')
      let backupData = data
      
      // Se for formato antigo, converter para novo formato
      if (data.version && !data.dados) {
        backupData = {
          id: crypto.randomUUID(),
          dataCriacao: Date.now(),
          versaoApp: data.version || '1.0.0',
          dataHora: new Date().toISOString(),
          dados: {
            produtos: data.produtos || [],
            funcionarios: data.funcionarios || [],
            movimentacoes: data.movimentacoes || [],
            users: data.users || [],
            auditLogs: data.auditLogs || [],
            settings: data.settings || [],
            pedidosAtivo: data.pedidosAtivo || [],
            pedidosArquivados: data.pedidosArquivados || [],
            counters: data.counters || []
          }
        }
      }

      // Se não tiver dados nem version, tentar criar estrutura mínima
      if (!backupData.dados && !backupData.version) {
        // Verificar se tem alguma estrutura de dados reconhecível
        const possibleKeys = ['produtos', 'funcionarios', 'movimentacoes', 'users', 'auditLogs', 'settings']
        const hasRecognizableData = possibleKeys.some(key => Array.isArray(data[key]))
        
        if (hasRecognizableData) {
          backupData = {
            id: crypto.randomUUID(),
            dataCriacao: Date.now(),
            versaoApp: '1.0.0',
            dataHora: new Date().toISOString(),
            dados: {}
          }
          possibleKeys.forEach(key => {
            if (Array.isArray(data[key])) {
              backupData.dados[key] = data[key]
            }
          })
        } else {
          throw new Error('Arquivo de backup inválido: formato não reconhecido. O arquivo deve conter dados de produtos, funcionários ou movimentações.')
        }
      }

      // Validar formato
      if (!backupData.dados) {
        throw new Error('Arquivo de backup inválido: formato não reconhecido.')
      }

      // MODO LOCAL: Restaurar apenas localmente (sem sincronização)
      await restoreBackup(backupData, { usuario: session })
      setToastType('success')
      setToast('Backup restaurado localmente com sucesso!')
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao restaurar.')
    } finally {
      setBusy(false)
    }
  }

  async function handleBackupNow() {
    setBusy(true)
    try {
      const backup = await createBackup({ usuario: session })
      const result = await downloadBackup(backup)
      
      if (result.savedToFolder) {
        setToastType('success')
        setToast(`Backup salvo na pasta: ${result.fileName}`)
      } else {
        setToastType('success')
        setToast('Backup criado e baixado com sucesso.')
      }
      
      const list = await listBackups()
      setBackups(list)
      
      // Se tiver pasta configurada, atualizar lista também
      if (backupFolder) {
        const folderBackups = await listFolderBackups()
        setBackups(folderBackups)
      }
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao criar backup.')
    } finally {
      setBusy(false)
    }
  }

  async function handleDeleteBackup(id) {
    setConfirmModal({
      open: true,
      type: 'danger',
      title: 'Excluir Backup',
      message: 'Tem certeza que deseja excluir este backup? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        setBusy(true)
        try {
          await deleteBackup(id)
          setToastType('success')
          setToast('Backup excluído.')
          const list = await listBackups()
          setBackups(list)
        } catch (err) {
          setToastType('error')
          setToast(err?.message || 'Falha ao excluir.')
        } finally {
          setBusy(false)
        }
      }
    })
  }

  async function handleSelectBackupFolder() {
    setBusy(true)
    try {
      const handle = await selectBackupFolder()
      setBackupFolder(handle)
      setToastType('success')
      setToast('Pasta de backup configurada com sucesso.')
      
      // Carregar backups da pasta
      const folderBackups = await listFolderBackups()
      setBackups(folderBackups)
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao selecionar pasta.')
    } finally {
      setBusy(false)
    }
  }

  async function handleRestoreFolderBackup(backup) {
    setConfirmModal({
      open: true,
      type: 'warning',
      title: 'Restaurar Backup',
      message: 'Tem certeza que deseja restaurar este backup? Todos os dados atuais serão substituídos.',
      confirmText: 'Restaurar',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        setBusy(true)
        try {
          await restoreFolderBackup(backup, { usuario: session })
          setToastType('success')
          setToast('Backup restaurado. Recarregue a página.')
        } catch (err) {
          setToastType('error')
          setToast(err?.message || 'Falha ao restaurar.')
        } finally {
          setBusy(false)
        }
      }
    })
  }

  async function handleDeleteFolderBackup(backup) {
    setConfirmModal({
      open: true,
      type: 'danger',
      title: 'Excluir Backup',
      message: 'Tem certeza que deseja excluir este backup? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        setBusy(true)
        try {
          await deleteFolderBackup(backup)
          setToastType('success')
          setToast('Backup excluído.')
          const folderBackups = await listFolderBackups()
          setBackups(folderBackups)
        } catch (err) {
          setToastType('error')
          setToast(err?.message || 'Falha ao excluir.')
        } finally {
          setBusy(false)
        }
      }
    })
  }

  async function updateBackupSettings(key, value) {
    const newSettings = { ...backupSettings, [key]: value }
    setBackupSettings(newSettings)
    await saveBackupSettings(newSettings)
    setToastType('success')
    setToast('Configurações salvas.')
  }

  function clearDatabase() {
    setConfirmModal({
      open: true,
      type: 'danger',
      title: 'Limpar Banco de Dados',
      message: 'Tem certeza que deseja limpar TODO o banco de dados? Todos os dados serão perdidos e esta ação não pode ser desfeita!',
      confirmText: 'Limpar Tudo',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        setBusy(true)
        try {
          await db.delete()
          setToastType('success')
          setToast('Banco de dados limpo. A página será recarregada.')
          setTimeout(() => window.location.reload(), 1500)
        } catch (err) {
          setToastType('error')
          setToast(err?.message || 'Falha ao limpar banco de dados.')
        } finally {
          setBusy(false)
        }
      }
    })
  }

  async function handleLimparBancoOnline() {
    setLimparErro('')
    if (!limparSenha) { setLimparErro('Digite o código de confirmação.'); return }
    if (limparSenha !== 'LIMPAR-TUDO') {
      setLimparErro('Código incorreto. Digite exatamente: LIMPAR-TUDO')
      return
    }
    setLimparBusy(true)
    try {
      const empresaId = session?.empresaId
      if (!empresaId) {
        setLimparErro('Erro: não foi possível identificar a empresa logada.')
        return
      }
      
      // Apagar apenas dados da empresa logada (multi-tenant seguro)
      const tabelas = ['movimentacoes', 'produtos', 'funcionarios', 'compras_pendentes', 'historico_pedidos', 'pedidos_ativo']
      for (const t of tabelas) {
        await supabase.from(t).delete().eq('empresa_id', empresaId)
      }
      
      // Resetar contador local de pedidos para voltar a PED-001
      try {
        const { db } = await import('../data/localDb.js')
        await db.counters.put({
          id: 'orderNumber',
          value: 0,
          updatedAt: Date.now()
        })
        console.log('[Settings] Contador de pedidos resetado para 0')
      } catch (err) {
        console.warn('[Settings] Erro ao resetar contador local:', err)
      }
      
      setLimparOnlineModal(false)
      setLimparSenha('')
      setToastType('success')
      setToast('Dados da empresa limpos com sucesso! A numeração de pedidos voltou a PED-001')
    } catch (err) {
      setLimparErro(err?.message || 'Erro ao limpar banco.')
    } finally {
      setLimparBusy(false)
    }
  }

  async function installApp() {
    // Verificar se o app já está instalado
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone === true ||
                        document.referrer.includes('android-app://')
    
    if (isStandalone) {
      setToastType('warning')
      setToast('O app já está instalado no seu dispositivo!')
      return
    }
    
    // Tentar usar o prompt nativo se disponível
    const deferredPrompt = window.deferredPrompt
    
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt()
        const { outcome } = await deferredPrompt.userChoice
        if (outcome === 'accepted') {
          setToastType('success')
          setToast('✅ App instalado com sucesso!')
        } else {
          setToastType('info')
          setToast('Instalação cancelada.')
        }
        window.deferredPrompt = null
        setInstallStatus(prev => ({ ...prev, canInstall: false }))
        return
      } catch (error) {
        console.error('Erro ao tentar instalar app:', error)
      }
    }
    
    // Se não tem prompt nativo, tentar método alternativo para Chrome
    if ('chrome' in window && window.chrome.app) {
      // Chrome desktop - tentar forçar o prompt
      try {
        // Simular um clique no botão de instalação do Chrome
        const event = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
        document.dispatchEvent(event)
        
        setToastType('info')
        setToast('📱 Para instalar: clique no ícone ➕ na barra de endereço do Chrome')
      } catch (e) {
        setToastType('info')
        setToast('📱 Chrome: Menu (⋮) → Instalar "CAIXA MESTRE"')
      }
    } else if (/Android/.test(navigator.userAgent)) {
      setToastType('info')
      setToast('📱 Chrome Android: Menu (⋮) → "Adicionar à tela inicial"')
    } else if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      setToastType('info')
      setToast('📱 iPhone: Botão Compartilhar (⬆️) → "Adicionar à Tela Inicial"')
    } else {
      setToastType('info')
      setToast('📱 Use o menu do navegador para adicionar à tela inicial')
    }
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />

      {/* Modal Limpar Banco Online */}
      {limparOnlineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-2xl cm-card p-6 space-y-4">
            <div className="text-center">
              <div className="text-3xl mb-2">⚠️</div>
              <div className="text-base font-bold text-red-300">Limpar Dados na Nuvem</div>
              <div className="mt-2 text-xs text-white/70">
                Esta ação irá <span className="text-red-400 font-semibold">apagar permanentemente</span> todos os dados da <span className="text-amber-300 font-semibold">empresa logada</span>:
              </div>
              <div className="mt-2 text-xs text-white/50 text-left space-y-1 bg-white/5 rounded-xl p-3">
                <div>• Todos os produtos</div>
                <div>• Todos os funcionários</div>
                <div>• Todas as movimentações</div>
                <div>• Compras pendentes e histórico</div>
              </div>
              <div className="mt-3 text-xs text-amber-300 font-semibold">
                Esta ação NÃO pode ser desfeita!
              </div>
            </div>
            <div>
              <div className="text-xs text-white/60 mb-1">Digite <span className="text-red-300 font-mono font-bold">LIMPAR-TUDO</span> para confirmar</div>
              <input
                type="text"
                value={limparSenha}
                onChange={e => { setLimparSenha(e.target.value); setLimparErro('') }}
                placeholder="LIMPAR-TUDO"
                className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none border border-white/10 focus:border-red-500/50 font-mono"
                autoFocus
                autoComplete="off"
              />
              {limparErro && (
                <div className="mt-1 text-xs text-red-400">{limparErro}</div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setLimparOnlineModal(false); setLimparSenha(''); setLimparErro('') }}
                disabled={limparBusy}
                className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-sm font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleLimparBancoOnline}
                disabled={limparBusy || !limparSenha}
                className="flex-1 rounded-xl bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {limparBusy ? 'Limpando…' : 'Confirmar e Limpar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      <BackButton />

      <div className="rounded-2xl cm-card p-4">
        <div className="text-sm font-semibold">Configurações</div>
        <div className="mt-1 text-xs text-white/70">Backup, administração e informações.</div>
      </div>

      {isAdmin && (
        <div className="rounded-2xl cm-card p-4">
          <div className="text-xs font-semibold">Backup e Restauração</div>
          
          {/* Configurações de Backup Automático */}
          <div className="mt-3 rounded-xl bg-white/5 p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Backup Automático</div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={backupSettings.enabled}
                  onChange={(e) => updateBackupSettings('enabled', e.target.checked)}
                  className="peer sr-only"
                />
                <div className="peer-checked:bg-accent-600 peer-checked:after:translate-x-full peer-focus:ring-accent-300 h-5 w-9 rounded-full bg-white/20 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all"></div>
              </label>
            </div>
            {backupSettings.enabled && (
              <div className="mt-2">
                <div className="text-xs text-white/60 mb-1">Intervalo</div>
                <select
                  value={backupSettings.interval}
                  onChange={(e) => updateBackupSettings('interval', e.target.value)}
                  className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm"
                >
                  <option value="hourly">A cada hora</option>
                  <option value="daily">Diário</option>
                  <option value="weekly">Semanal</option>
                </select>
                {backupSettings.lastBackup && (
                  <div className="mt-1 text-[10px] text-white/50">
                    Último backup: {new Date(backupSettings.lastBackup).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-3 space-y-2">
            <button
              disabled={busy}
              onClick={handleBackupNow}
              className="w-full rounded-xl bg-emerald-600/90 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Criando…' : 'Criar backup agora'}
            </button>
            <button
              disabled={busy}
              onClick={handleSelectBackupFolder}
              className="w-full rounded-xl bg-blue-600/90 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Selecionando…' : backupFolder ? '📁 Pasta configurada' : '📁 Selecionar pasta de backup'}
            </button>
            <label className="block w-full rounded-xl bg-white/10 px-3 py-3 text-center text-sm font-semibold">
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) importBackup(f)
                  e.target.value = ''
                }}
              />
              Restaurar backup (JSON)
            </label>
            <button
              onClick={() => setShowBackups(!showBackups)}
              className="w-full rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold"
            >
              {showBackups ? 'Ocultar backups salvos' : 'Ver backups salvos'}
            </button>
            {showBackups && backups.length > 0 && (
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {backups.map((backup) => (
                  <div key={backup.id} className="rounded-lg bg-white/5 p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{new Date(backup.dataCriacao).toLocaleString('pt-BR')}</div>
                        <div className="text-white/50">
                          v{backup.versaoApp}
                          {backup.isFolderBackup && ' 📁'}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {backup.isFolderBackup && (
                          <button
                            onClick={() => handleRestoreFolderBackup(backup)}
                            disabled={busy}
                            className="rounded-lg bg-emerald-500/20 px-2 py-1 text-[10px] text-emerald-300 hover:bg-emerald-500/30"
                          >
                            Restaurar
                          </button>
                        )}
                        <button
                          onClick={() => backup.isFolderBackup ? handleDeleteFolderBackup(backup) : handleDeleteBackup(backup.id)}
                          disabled={busy}
                          className="rounded-lg bg-red-500/20 px-2 py-1 text-[10px] text-red-300 hover:bg-red-500/30"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-2 text-xs text-white/60">Atenção: restaurar substitui todos os dados atuais.</div>
        </div>
      )}

      {isAdmin && (
        <div className="rounded-2xl cm-card p-4">
          <div className="text-xs font-semibold">Administração</div>
          <div className="mt-3 space-y-2">
            <div className="rounded-xl bg-white/5 p-3">
              <div className="text-xs font-semibold mb-2">Módulo Financeiro</div>
              <div className="flex items-center justify-between text-xs">
                <span>Ativar Financeiro</span>
                <input type="checkbox" checked={!!finSettings.enabled} onChange={async (e) => {
                  const ns = { ...finSettings, enabled: e.target.checked }
                  setFinSettings(ns)
                  await saveFinanceSettings(ns)
                  setToastType('success'); setToast('Configuração salva')
                }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span>Ocultar valores (R$) na interface</span>
                <input type="checkbox" checked={!!finSettings.hideValues} onChange={async (e) => {
                  const ns = { ...finSettings, hideValues: e.target.checked }
                  setFinSettings(ns)
                  await saveFinanceSettings(ns)
                  setToastType('success'); setToast('Configuração salva')
                }} />
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Link className="rounded-xl bg-white/10 px-3 py-3 text-center text-sm font-semibold text-white" to="/app/auditoria">
              Log de Auditoria
            </Link>
            <Link className="rounded-xl bg-white/10 px-3 py-3 text-center text-sm font-semibold text-white" to="/app/usuarios">
              Gerenciar Usuários
            </Link>
          </div>
          <div className="mt-3 space-y-2">
            <button
              disabled={busy}
              onClick={clearDatabase}
              className="w-full rounded-xl bg-red-600/90 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Limpando…' : '🗑️ Limpar Banco Local'}
            </button>
            <button
              disabled={busy}
              onClick={() => { setLimparSenha(''); setLimparErro(''); setLimparOnlineModal(true) }}
              className="w-full rounded-xl bg-red-900/80 border border-red-500/40 px-3 py-3 text-sm font-semibold text-red-200 disabled:opacity-60"
            >
              ☁️ Limpar Dados na Nuvem
            </button>
            <button
              onClick={installApp}
              disabled={busy || installStatus.installed}
              className={`w-full rounded-xl px-3 py-3 text-sm font-semibold text-white disabled:opacity-60 ${
                installStatus.installed 
                  ? 'bg-emerald-600/90' 
                  : 'bg-blue-600/90'
              }`}
            >
              {installStatus.installed 
                ? '✅ App Instalado' 
                : '📲 Instalar App'
              }
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
